import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { musicList } from './MusicList';
import './MusicOptionsList.css';
import {
  setTimerAudioState,
  setMusicSelectedOption,
  setCurrentPreviewMusic,
  setPreviousSelectedMusicOption,
} from './actions';
import { selectedTimerMusicOption, selectCurrentPreviewMusic } from './reducer';
import CustomTooltip from '../CustomTooltip/CustomTooltip';
import {
  IconCheckmarkSmall,
  IconChevronUpSmall,
  IconPlayFill,
  IconVolumeMuteFill,
} from '@zoom/icons-react';

/** This component shows and manages music options list. It sets and preview music options. */
export default function MusicOptionsList({
  refs,
  source,
  currentAlarmToneSetting,
  setCurrentAlarmToneSetting,
}) {
  const dispatch = useDispatch();
  const selectedOption = useSelector(selectedTimerMusicOption);
  const currentPreviewMusic = useSelector(selectCurrentPreviewMusic);
  const [showList, setShowList] = useState(false);
  const popOverRef = useRef(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const listRef = useRef([]);

  useEffect(() => {
    function handleClickOutsidePopOver(event) {
      if (popOverRef.current && !popOverRef.current.contains(event.target)) {
        setShowList(false);
      }
    }
    document.addEventListener('click', handleClickOutsidePopOver);
    return () => {
      document.removeEventListener('click', handleClickOutsidePopOver);
    };
  }, []);

  useEffect(() => {
    if (listRef.current[focusedIndex]) {
      listRef.current[focusedIndex].focus();
    }
  }, [focusedIndex]);

  function optionsButtonClick() {
    setShowList(!showList);
    if (!showList) {
      setFocusedIndex(0);
    }
  }

  const setMusicOption = useCallback(
    (option, id) => {
      // turn on audio if some other option is selected
      if (option !== 'Off') {
        dispatch(setTimerAudioState(true));
      } else {
        // if audio is off, store current selected option as previous value
        dispatch(
          setPreviousSelectedMusicOption({
            name: selectedOption.name,
            id: selectedOption.id,
          })
        );
      }
      if (source === 'Queue') {
        setCurrentAlarmToneSetting({ name: option, id: id });
      } else {
        window.localStorage.setItem(
          'preferredMusic',
          JSON.stringify({ name: option, id: id })
        );
        dispatch(setMusicSelectedOption({ name: option, id: id }));
      }
      setShowList(false);
    },
    [dispatch, selectedOption]
  );

  /** This function is used to preview music. Before playing music pause the existing one, if not both music files will play at a time*/
  const playMusic = (id, e) => {
    e.stopPropagation();
    pauseMusic(currentPreviewMusic);

    let currentSelectedMusicRef = refs[id].ref;
    if (currentSelectedMusicRef?.current?.src) {
      dispatch(setCurrentPreviewMusic(id));
      currentSelectedMusicRef.current.muted = false;
      currentSelectedMusicRef.current
        .play()
        .then(() => {
          setIsPreviewPlaying(true);
        })
        .catch((error) => {
          console.log(error);
        });
      currentSelectedMusicRef.current.addEventListener('ended', () => {
        setIsPreviewPlaying(false);
      });
    }
  };

  const pauseMusic = (id) => {
    let currentPreviewMusicRef = refs[id].ref;
    if (currentPreviewMusicRef?.current?.src) {
      currentPreviewMusicRef.current.pause();
      currentPreviewMusicRef.current.currentTime = 0;
    }
  };

  const handleKeyDown = (e, option) => {
    switch (e.key) {
      case 'Tab':
        setFocusedIndex((prevIndex) => {
          if (prevIndex !== musicList.length) {
            return prevIndex + 1;
          } else {
            setShowList(false);
            return prevIndex;
          }
        });
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prevIndex) =>
          prevIndex === musicList.length ? -1 : prevIndex + 1
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prevIndex) =>
          prevIndex === -1 ? musicList.length : prevIndex - 1
        );
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (option === 'Off') {
          dispatch(setTimerAudioState(false));
          setMusicOption('Off', -1);
        } else if (option === 'Beep') {
          setMusicOption('Beep', 0);
        } else {
          setMusicOption(option.name, option.id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowList(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={popOverRef}>
      <button
        className={`musicSelectedOption ${showList ? 'showList' : ''}`}
        onClick={optionsButtonClick}
        type='button'
        aria-label='Alarm Sound Dropdown Menu'
      >
        <p
          className={`${
            selectedOption.name === 'Off' ? 'smallerText' : 'selectedOptionText'
          }`}
        >
          {source === 'Queue'
            ? currentAlarmToneSetting.name
            : selectedOption.name}
        </p>
        <span>
          <IconChevronUpSmall
            style={{
              color: '#F7F9FA',
              fontSize: '16px',
            }}
          />
        </span>
      </button>
      {showList && (
        <div className='optionsPopOver'>
          <div className='listHeading'>Alarm when the timer ends:</div>
          <div className='musicOptions'>
            <ul>
              <li
                className='musicOption'
                key='music_off'
                tabIndex={0}
                ref={(el) => (listRef.current[-1] = el)}
                onKeyDown={(e) => handleKeyDown(e, 'Off')}
              >
                <div
                  className={`${
                    (source === 'Queue'
                      ? currentAlarmToneSetting.name
                      : selectedOption.name) === 'Off'
                      ? 'selectedOption'
                      : 'notselectedOption'
                  }`}
                >
                  <IconCheckmarkSmall
                    style={{
                      color: '#4793F1',
                      fontSize: '16px',
                    }}
                  />
                </div>
                <div className='musicOffIcon'>
                  <IconVolumeMuteFill
                    style={{
                      color: '#F7F9FA',
                      cursor: 'pointer',
                      fontSize: '16px',
                    }}
                    onClick={() => {
                      dispatch(setTimerAudioState(false));
                      setMusicOption('Off', -1);
                    }}
                  />
                </div>
                <span
                  onClick={() => {
                    dispatch(setTimerAudioState(false));
                    setMusicOption('Off', -1);
                  }}
                >
                  Off
                </span>
                <button
                  onClick={() => {
                    dispatch(setTimerAudioState(false));
                    setMusicOption('Off', -1);
                  }}
                  className='offSetButton'
                >
                  Set
                </button>
              </li>
              {musicList.map((option, index) => {
                const isSelected =
                  (source === 'Queue'
                    ? currentAlarmToneSetting.id
                    : selectedOption.id) === option.id;
                return (
                  <li
                    className='musicOption'
                    key={option.id}
                    tabIndex={0}
                    data-id={index}
                    ref={(el) => (listRef.current[index] = el)}
                    onKeyDown={(e) => handleKeyDown(e, option)}
                  >
                    <div
                      className={
                        isSelected ? 'selectedOption' : 'notselectedOption'
                      }
                    >
                      <IconCheckmarkSmall
                        style={{
                          color: '#4793F1',
                          fontSize: '16px',
                        }}
                      />
                    </div>
                    <span
                      onClick={() => setMusicOption(option.name, option.id)}
                    >
                      {option.name}
                    </span>
                    <button
                      onClick={() => setMusicOption(option.name, option.id)}
                      className='setMusicButton'
                    >
                      Set
                    </button>
                    {isPreviewPlaying && currentPreviewMusic === option.id ? (
                      <div className='playIndicator-icon'>
                        <div className='vertical-bar vertical-bar-1'></div>
                        <div className='vertical-bar vertical-bar-2'></div>
                        <div className='vertical-bar vertical-bar-3'></div>
                      </div>
                    ) : (
                      <div className='musicPreviewButton'>
                        <button
                          data-tooltip-id={option.id}
                          data-tooltip-delay-show={300}
                          data-tooltip-place='top'
                          onClick={(e) => playMusic(option.id, e)}
                          className='musicPlayIcon'
                          key={option.id}
                        >
                          <IconPlayFill
                            style={{
                              color: '#F7F9FA',
                              fontSize: '16px',
                            }}
                          />
                        </button>
                        <CustomTooltip text='Play for me' id={option.id} />
                      </div>
                    )}
                  </li>
                );
              })}
              <li
                className='musicOption'
                key='default_music_option'
                tabIndex={0}
                ref={(el) => (listRef.current[musicList.length] = el)}
                data-id={musicList.length + 1}
                onKeyDown={(e) => handleKeyDown(e, 'Beep')}
              >
                <div
                  className={`${
                    (source === 'Queue'
                      ? currentAlarmToneSetting.name
                      : selectedOption.name) === 'Beep'
                      ? 'selectedOption'
                      : 'notselectedOption'
                  }`}
                >
                  <IconCheckmarkSmall
                    style={{
                      color: '#4793F1',
                      fontSize: '16px',
                    }}
                  />
                </div>
                <span onClick={() => setMusicOption('Beep', 0)}>Beep</span>
                <button
                  onClick={() => setMusicOption('Beep', 0)}
                  className='setMusicButton'
                >
                  Set
                </button>
                {isPreviewPlaying && currentPreviewMusic === 0 ? (
                  <div className='playIndicator-icon'>
                    <div className='vertical-bar vertical-bar-1'></div>
                    <div className='vertical-bar vertical-bar-2'></div>
                    <div className='vertical-bar vertical-bar-3'></div>
                  </div>
                ) : (
                  <div className='musicPreviewButton'>
                    <button
                      data-tooltip-id={0}
                      data-tooltip-delay-show={300}
                      data-tooltip-place='top'
                      aria-label='preview-button'
                      onClick={(e) => playMusic(0, e)}
                      className='musicPlayIcon'
                      key={0}
                    >
                      <IconPlayFill
                        style={{
                          color: '#F7F9FA',
                          fontSize: '16px',
                        }}
                      />
                    </button>
                    <CustomTooltip text='Play for me' id={0} />
                  </div>
                )}
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
